export function formatTemperature(value: number | undefined): string {
  return value === undefined ? '—' : `${value.toFixed(1)} °C`;
}

export function formatPercent(value: number | undefined, digits = 0): string {
  return value === undefined ? '—' : `${value.toFixed(digits)} %`;
}

export function formatLux(value: number | undefined): string {
  return value === undefined ? '—' : `${Math.round(value).toLocaleString()} lux`;
}

export function formatSignal(dbm: number | undefined, quality?: number): string {
  if (dbm === undefined && quality === undefined) return '—';
  const parts: string[] = [];
  if (dbm !== undefined) parts.push(`${dbm} dBm`);
  if (quality !== undefined) parts.push(`${quality} %`);
  return parts.join(' · ');
}

export function formatDateTime(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/** "3 min ago", "2 h ago", "5 d ago". */
export function timeAgo(iso: string | undefined, now: Date = new Date()): string {
  if (!iso) return 'never';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const diff = Math.max(0, now.getTime() - t);
  const s = Math.round(diff / 1000);
  if (s < 45) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d} d ago`;
  const mo = Math.round(d / 30);
  return mo < 12 ? `${mo} mo ago` : `${Math.round(mo / 12)} y ago`;
}

export function formatDuration(minutes: number | undefined): string {
  if (minutes === undefined || !Number.isFinite(minutes)) return '—';
  const m = Math.max(0, Math.round(minutes));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h} h ${m % 60} min`;
  return `${Math.floor(h / 24)} d ${h % 24} h`;
}

export function minutesSince(iso: string | undefined, now: Date = new Date()): number | undefined {
  if (!iso) return undefined;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? undefined : (now.getTime() - t) / 60000;
}

/** 1284 -> "1,284", 12900 -> "12.9K". */
export function compactNumber(value: number): string {
  if (Math.abs(value) < 10000) return value.toLocaleString();
  return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(
    value,
  );
}

export function slugify(input: string): string {
  return input
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

export function macWithColons(mac: string | undefined): string {
  if (!mac) return '—';
  const hex = mac.replace(/[^0-9a-f]/gi, '');
  return hex.length === 12 ? (hex.match(/.{2}/g) ?? []).join(':').toUpperCase() : mac;
}
